import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import parse, { domToReact } from 'html-react-parser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';

import { Article, ArticleUtil } from '@/modules/articles/model/article';
import { ConceptItemConstants } from '@/modules/articles/model/concept-item';
import { OpinionMap } from '@/modules/articles/model/opinions/opinion-map';
import { opinionsApi } from '@/modules/articles/api';
import { useWidgetNavigate } from '@/core/widgets/export';
import { AgedDateFormat } from '@/core/date/export';
import { EmptyStateIndicator } from '@/shared/components/export';
import BookmarkButton from '@/modules/articles/components/bookmarks/bookmark-button';
import articleListStyles from '@/shared/components/flash-highlight.module.scss';
import SeriesScroller from '@/modules/articles/components/scrollers/pinned-series/pinned-series-scroller';

export default function PinnedSeriesList({
  series,
  isStickyNote = false,
  currentPage,
  setCurrentPage,
}: {
  series: string[];
  isStickyNote: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}) {
  const [articles, setArticles] = useState<Article[]>();
  const [opinions, setOpinions] = useState<OpinionMap>({});
  const navigate = useWidgetNavigate();

  useEffect(() => {
    if (articles)
      opinionsApi.getOpinionsOfArticles(...articles.map(value => value.uri)).then(setOpinions);
  }, [articles]);

  const isBelled = (a: Article) => {
    return !!a.genre.find(value => {
      // eslint-disable-next-line
      return `${value.scheme}:${value.code}` == ConceptItemConstants.BELL;
    });
  };

  const flash = (index: number, date: string) => {
    if (index === 0 && dayjs(date).isAfter(dayjs().subtract(5, 'minute')))
      return articleListStyles.flashArticle;

    return '';
  };

  const renderHtmlWithBellIcon = (html: string) => {
    return parse(html, {
      replace: (domNode: any) => {
        if (domNode.name === 'li' && domNode.attribs && domNode.attribs['data-belled'] === 'true') {
          // Add the bell icon inside the li element if data-belled="true"
          return (
            <li {...domNode.attribs}>
              <FontAwesomeIcon icon={faBell} /> {domToReact(domNode.children)}
            </li>
          );
        }
      },
    });
  };

  return (
    <SeriesScroller
      includeGenres={series}
      onArticlesChange={setArticles}
      onPageChange={setCurrentPage}
      initialPage={currentPage}
      dark={!isStickyNote}
    >
      <table className={'table overflow-auto table-sm table-hover'}>
        <tbody>
          {articles?.map((value, index) => {
            const flashClass = flash(index, value.firstcreated);

            const isHeadline = ArticleUtil.hasGenre(value, 'HEADLINE');

            return (
              <tr key={'mw-' + value.uri} className={`${flashClass}`}>
                <td className={`text-nowrap text-muted text-black ${flashClass}`}>
                  <AgedDateFormat format={'h:mm a[:]'} date={value.firstcreated} />
                </td>
                <td className={`w-100 ${flashClass}`}>
                  <div className={'d-flex justify-content-between '}>
                    {/*  eslint-disable-next-line   */}
                    <a
                      className={`text-decoration-none ${isBelled(value) ? 'link-danger' : isStickyNote ? 'text-black' : 'text-white'}`}
                      onClick={() => {
                        if (currentPage === 0 && index === 0) {
                          navigate('/');
                        } else {
                          navigate('/list/' + value.uri);
                        }
                      }}
                    >
                      {isHeadline ? (
                        <div>{renderHtmlWithBellIcon(value.body_xhtml)}</div>
                      ) : (
                        <>
                          {isBelled(value) && <FontAwesomeIcon icon={faBell} />} {value.headline}
                        </>
                      )}
                    </a>
                    <div className={`text-end ${flashClass} flex-shrink-0`}>
                      <BookmarkButton article={value} providedOpinions={opinions} />
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/*  eslint-disable-next-line  */}
      {articles?.length == 0 && (
        <div className={'w-100 h-75 d-flex flex-column align-items-center justify-content-around'}>
          <div className={'w-25'}>
            <EmptyStateIndicator text={'No More items'} />
          </div>
        </div>
      )}
    </SeriesScroller>
  );
}
